import React, { memo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { useSelector } from 'react-redux';

import { makeStyles } from '@material-ui/core/styles';
import { Button } from '@material-ui/core';
import { Button as ButtonCommon } from '@common/components';

import { Card, Typography, Table, TableBody, TableRow, TableCell } from '@material-ui/core';

import moment from 'moment';

import { useTranslation, useDetails, toast } from '@common/utils';
import { useSettings } from '@common/hooks';
import { Link } from 'react-router-dom';

import { ClearIcon } from '@common/components/Icon';

import { buildQuery } from '../../../../ui-common/utils';

import http from '@common/http';

import { keyBy, uniq } from 'lodash';

function BookingCard({
	bookingId,
	booking,
	details = {},
	removeBooking,
	proceedToCheckout,
}) {
	const classes = useStyles();
	const { locale } = useSelector(state => state.locale);
	const tr = useTranslation();

	const { currencySymbol } = useSettings();

	const {
		totalFees, totalBeds,
		discounts = [], uninvoicedTotal, totalDeposits,
		total, totalMandatory, productsGrouped = [], totalUpfront,
		products = [],
	} = details;

	const isInitial = (details.products || []).every(p => !p.order);

	const totalUpgrades = productsGrouped.filter(p => p.upgrade).reduce((acc, upgrade) => {
		if (upgrade.productUpgradeType === 'recurring') {
			acc += upgrade.price;
		} else {
			acc += upgrade.total;
		}

		return acc;
	}, 0);

	const { details: product } = useDetails(`/api/kiosks/student-housing/products/${booking.product}`, {
		from: '',
		price: 0,
		product: '',
		productAssetPersonCapacity: 0,
		productAssetType: '',
		productPersonCapacity: 0,
		quantity: 0,
		reservation: [],
		to: '',
		total: 0,
		totalServices: 0,
		i18n: {},
	}, [booking.product]);

	const isDorm = product.assetType === 'dorm';

	const [productById, setProductById] = useState({});

	const trackProceedToCheckout = () => {
		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push({ 'event': 'Proceed to Checkout' });
	};

	useEffect(() => {

		Promise
			.all(uniq(details.products, 'product').map(p => http.get(`/api/kiosks/student-housing/products/${p.product}`).catch(() => ({}))))
			.then(responses => responses.map(response => response.data).filter(Boolean))
			.then(products => setProductById(keyBy(products, '_id')))
			.catch(error => {
				console.error(error);
				toast(error);
			});

	}, [details.productsGrouped]);

	return (
		<Card className={classes.card}>
			<div className={classes.product}>
				<Link to={`/${booking.product}${buildQuery({ from: booking.from, to: booking.to, seasons: booking.seasons })}`} className={classes.link}>
					<div
						className={classes.image}
						style={{ backgroundImage: product.image ? `url(/images/${product.image})` : 'none' }}
					/>
				</Link>
				<div className={classes.info}>
					<div>
						<Typography variant="h4" className={classes.headline}>
							{product?.i18n[locale]?.title || product.title}
						</Typography>
					</div>
					{booking.from && booking.to && <div className={classes.dates}>
						<div>
							<b>{tr('Check-in')}</b>
							&nbsp;
							{moment(booking.from).format('MMM DD, YYYY')}
						</div>
						<div>
							<b>{tr('Check-out')}</b>
							&nbsp;
							{moment(booking.to).format('MMM DD, YYYY')}
						</div>
					</div>}
					{removeBooking && !booking.order.length && !booking.fee && (
						<div className={classes.removeBox}>
							<Button className={classes.removeButton} onClick={() => removeBooking()}>
								<ClearIcon className={classes.icon} />
								{tr('remove')}
							</Button>
						</div>
					)}
				</div>
			</div>

			<div className={classes.tableContainer}>
				<Table>
					<TableBody>
						<TableRow>
							<TableCell className={`${classes.col} ${classes.section}`} colSpan="3">
								{tr((booking.from && booking.to) ? isDorm ? 'Beds' : 'Rooms' : 'Items')}
							</TableCell>
						</TableRow>
						<TableRow>
							{booking.from && booking.to && <TableCell className={`${classes.col} ${classes.first}`}>1 {tr(isDorm ? 'bed' : 'room')}</TableCell>}
							{(!booking.from || !booking.to) && <TableCell className={`${classes.col} ${classes.first}`}>1 {tr('item')}</TableCell>}
							<TableCell className={classes.col} align="right"></TableCell>
							<TableCell className={`${classes.col} ${classes.last}`} align="right">{booking.price} {currencySymbol}</TableCell>
						</TableRow>
						<TableRow>
							<TableCell className={`${classes.col} ${classes.colQTY} ${classes.first}`}>{tr('Quantity')}</TableCell>
							<TableCell className={`${classes.col} ${classes.colQTY}`} align="right"></TableCell>
							<TableCell className={`${classes.col} ${classes.colQTY} ${classes.last}`} align="right">
								{booking.quantityMonths > 0
									? <>{booking.quantityMonths} {tr('months')}</>
									: booking.quantity
								}
							</TableCell>
						</TableRow>
					</TableBody>
					{/* {totalRooms > 0 && <Row name="Accomodation" value={totalRooms} />} */}
					{totalBeds > 0 && <Row name="Beds" value={totalBeds} />}
					{totalFees > 0 && <Row name="Fees" value={totalFees} />}
					{((totalMandatory > 0 && (totalUpfront - totalMandatory !== totalDeposits)) || totalUpgrades > 0) && <Row name={tr('Extras')} />}
					{productsGrouped.length > 0 && (productsGrouped || []).map((p, i) => {
						return (
							<>
								{p.mandatoryBookingProducts?.length > 0 &&
									<>
										{p.mandatoryBookingProducts.filter(p => p.productMandatoryType !== 'onetime-pay-upfront').map(p =>
											<TableRow key={i} className={classes.subRow}>
												<TableCell style={{ fontSize: 'small', padding: 0 }} className={`${classes.col} ${classes.first}`}>&nbsp;&nbsp;&nbsp;&nbsp;{productById[p.product]?.i18n[locale]?.title || productById[p.product]?.title}</TableCell>
												<TableCell style={{ fontSize: 'small', padding: 0 }} className={classes.col}></TableCell>
												<TableCell style={{ fontSize: 'small', padding: 0 }} className={`${classes.col} ${classes.last}`} align="right">{p.total} {currencySymbol}/{p.productMandatoryType === 'recurring' ? tr('month') : tr('unit')}</TableCell>
											</TableRow>,
										)}
									</>
								}
								{p.upgrade === true && !!productById[p.product] &&
									<TableRow key={i} className={classes.subRow}>
										<TableCell style={{ fontSize: 'small', padding: 0 }} className={`${classes.col} ${classes.first}`}>&nbsp;&nbsp;&nbsp;&nbsp;{productById[p.product]?.i18n[locale]?.title || productById[p.product]?.title}</TableCell>
										<TableCell style={{ fontSize: 'small', padding: 0 }} className={classes.col}></TableCell>
										<TableCell style={{ fontSize: 'small', padding: 0 }} className={`${classes.col} ${classes.last}`} align="right">{p.productUpgradeType === 'recurring' ? p.price : p.total} {currencySymbol}/{p.productUpgradeType === 'recurring' ? tr('month') : tr('unit')}</TableCell>
									</TableRow>
								}
							</>
						);
					})}
					{/* {totalDeposits > 0 && <Row name="Total" value={total} />} */}
					{discounts
						.filter(d => d.code && d.appliedAmount >= 0)
						.map((d, i) => <Row key={i} name={`Discount (${d.code})`} value={`-${d.appliedAmount}`} />)
					}

					{(totalDeposits > 0 || totalUpfront > 0) && <Row name={tr('Due today')} value={totalUpfront} bold />}

					{totalDeposits > 0 && <TableRow className={classes.indentedRow}>
						<TableCell className={`${classes.col} ${classes.first}`}>{tr('Deposits')}</TableCell>
						<TableCell className={classes.col}></TableCell>
						<TableCell className={`${classes.col} ${classes.last}`} align="right">{totalDeposits} {currencySymbol}</TableCell>
					</TableRow>}

					{products?.length > 0 && products.filter(p => p.productMandatoryType === 'onetime-pay-upfront').map((p, i) =>
						<TableRow key={i} className={classes.indentedRow}>
							<TableCell className={`${classes.col} ${classes.first}`}>{productById[p.product]?.i18n[locale]?.title || productById[p.product]?.title}</TableCell>
							<TableCell className={classes.col}></TableCell>
							<TableCell className={`${classes.col} ${classes.last}`} align="right">{p.total} {currencySymbol}</TableCell>
						</TableRow>,
					)}

					{discounts
						.filter(d => d.code && d.appliedAmount >= 0)
						.map((d, i) => (
							<TableRow key={i} className={classes.row}>
								<TableCell className={`${classes.col} ${classes.first}`}>{tr(`Discount (${d.code})`)}</TableCell>
								<TableCell className={classes.col}></TableCell>
								<TableCell className={`${classes.col} ${classes.last}`} align="right">&minus;{d.appliedAmount} {currencySymbol}</TableCell>
							</TableRow>
						))}

					{(!totalDeposits || totalDeposits === 0) && <Row name={tr(!isInitial && uninvoicedTotal > 0 ? 'Total paid' : 'Total')} value={total} bold />}
					{(totalDeposits > 0 || totalUpfront > 0) && !isInitial && uninvoicedTotal > 0 && <Row name={tr('Total due')} value={uninvoicedTotal} />}

				</Table>
				{(isInitial || uninvoicedTotal > 0) && proceedToCheckout && (
					<ButtonCommon
						wide={true}
						color="primary"
						component={Link}
						to={`/checkout/${bookingId}/?language=${locale}`}
						onClick={trackProceedToCheckout}
						style={{ marginTop: 25 }}
					>
						{tr('Proceed to checkout')}
					</ButtonCommon>
				)}
			</div>
		</Card>
	);
}

BookingCard.propTypes = {
	bookingId: PropTypes.string.isRequired,
	booking: PropTypes.object.isRequired,
	details: PropTypes.object.isRequired,
	upgrades: PropTypes.array,
	removeProduct: PropTypes.func,
	removeBooking: PropTypes.func,
	proceedToCheckout: PropTypes.bool,
};

const _Row = ({ name, value, bold = false, separator = true }) => {
	const classes = useStyles();
	const tr = useTranslation();

	const { currencySymbol } = useSettings();

	return (
		<>
			{separator && <Separator />}

			<TableRow className={classes.row}>
				<TableCell className={`${classes.col} ${classes.first}`}>
					{bold ? <b>{tr(name)}</b> : tr(name)}
				</TableCell>
				<TableCell className={classes.col}></TableCell>
				<TableCell className={`${classes.col} ${classes.last}`} align="right">
					{bold ? <b>{value} {value !== undefined && value !== null ? currencySymbol : ''}</b> : <>{value} {value !== undefined && value !== null ? currencySymbol : ''}</>}
				</TableCell>
			</TableRow>
		</>
	);
};
const Row = memo(_Row);
_Row.propTypes = {
	name: PropTypes.string,
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	bold: PropTypes.bool,
	separator: PropTypes.bool,
};

const _Separator = () => {
	const classes = useStyles();

	return (
		<TableRow>
			<TableCell className={`${classes.col} ${classes.sep}`} colSpan="3">
				<div className={classes.separator} />
			</TableCell>
		</TableRow>
	);
};
const Separator = memo(_Separator);


export default BookingCard;

const useStyles = makeStyles(theme => ({
	card: {
		borderRadius: '4px',
		border: `1px solid ${theme.palette.custom.borders}`,
		boxShadow: 'none',
		backgroundColor: theme.palette.custom.background,
	},
	headline: {
		marginBottom: theme.spacing(2),
	},
	product: {
		flexDirection: 'row',
		marginBottom: theme.spacing(3),
	},
	link: {
		flex: '0 0 calc(50% + 12px)',
		height: '180px',
	},
	image: {
		flex: 1,
		width: '100%',
		height: '200px',
		backgroundColor: theme.palette.custom.background,
		borderRadius: '4px 0 0',
		backgroundSize: 'cover',
		backgroundRepeat: 'no-repeat',
		backgroundPosition: 'center',

		[theme.breakpoints.down('sm')]: {
			display: 'none',
		},
	},
	removeBox: {
		marginTop: theme.spacing(3),
	},
	removeButton: {
		color: theme.palette.primary.light,
		textTransform: 'lowercase',
		padding: 0,
	},
	icon: {
		fontSize: '20px',
		marginRight: '8px',
	},
	info: {
		padding: theme.spacing(3),
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-between',
		paddingBottom: theme.spacing(1),
		flex: 1,
	},
	section: {
		paddingLeft: 0,
		fontSize: '18px',
		color: theme.palette.primary.light,
		fontWeight: 600,
	},
	tableContainer: {
		padding: theme.spacing(3),
		paddingTop: 0,
	},
	col: {
		paddingTop: theme.spacing(1),
		paddingBottom: theme.spacing(1),
		borderBottom: 0,
		fontSize: 15,
	},
	colQTY: {
		paddingTop: 0,
	},
	first: {
		paddingLeft: 0,
	},
	last: {
		paddingRight: 0,
	},
	sep: {
		paddingLeft: 0,
		paddingRight: 0,
	},
	separator: {
		height: '1px',
		backgroundColor: theme.palette.custom.separator,
	},
	span: {
		fontSize: '14px',
		position: 'relative',
		top: '-8px',
	},
	subRow: {
		fontSize: 'small',
	},
	indentedRow: {
		textIndent: '8px',
	},


	// header: {
	// 	display: 'flex',
	// 	flexDirection: 'row',
	// },
	// aside: {
	// 	flex: '0 0 auto',
	// },
	// content: {
	// 	padding: `${theme.spacing(2)}px ${theme.spacing(3)}px`,
	// },
	// actions: {
	// 	padding: '0 24px 24px',
	// },
	// media: {
	// 	height: '248px',
	// 	backgroundColor: theme.palette.custom.background,
	// },
	// props: {
	// 	display: 'flex',
	// 	flexDirection: 'row',
	// 	marginBottom: theme.spacing(2),
	// },
	// prop: {
	// 	padding: `5px ${theme.spacing(2)}px`,
	// 	border: `1px solid ${theme.palette.custom.borders}`,
	// 	color: theme.palette.primary.light,
	// 	borderRadius: '18px',
	// 	whiteSpace: 'nowrap',
	// 	'&:nth-child(n+2)': {
	// 		marginLeft: theme.spacing(2),
	// 	}
	// },
	// price: {
	// 	lineHeight: '2.1rem',
	// },
	// currency: {
	// 	color: theme.palette.primary.light,
	// 	paddingLeft: '4px',
	// },
}));
